import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import AppLayout from '../../components/AppLayout'

const STATE_META = {
  running: { label: '実行中', bg: '#fef3c7', text: '#92400e' },
  done:    { label: '完了',   bg: '#dcfce7', text: '#166534' },
  error:   { label: 'エラー', bg: '#fee2e2', text: '#991b1b' },
  aborted: { label: '中断',   bg: '#e5e7eb', text: '#4b5563' },
}

const JOB_TYPE_COLOR = {
  bigquery_sync:     { bg: '#eff6ff', text: '#1d4ed8' },
  reevaluate_recent: { bg: '#f5f3ff', text: '#6d28d9' },
  master_xlsx_sync:  { bg: '#ecfdf5', text: '#047857' },
}

function formatDuration(sec) {
  if (sec == null) return '—'
  if (sec < 60) return `${sec}秒`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}分${s}秒`
}

function RunRow({ r, i }) {
  const [expanded, setExpanded] = useState(false)
  const sm = STATE_META[r.state] || { label: r.state, bg: '#e4e4e4', text: '#555' }
  const jc = JOB_TYPE_COLOR[r.job_type] || { bg: '#f1f5f9', text: '#334155' }
  const hasMessage = r.error_message && r.error_message.length > 0
  const hasError = hasMessage && r.state === 'error'

  function handleAbort() {
    if (!confirm('このジョブを強制中断しますか？')) return
    router.post(`/job_runs/${r.id}/abort`)
  }

  return (
    <>
      <tr style={{ borderBottom: hasMessage && expanded ? 'none' : '1px solid #f1f5f9', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
        <td style={td}>{new Date(r.started_at).toLocaleString('ja-JP')}</td>
        <td style={td}>
          <span style={{ padding: '2px 7px', borderRadius: '4px', background: jc.bg, color: jc.text, fontSize: '11px', fontWeight: 600 }}>
            {r.label}
          </span>
        </td>
        <td style={td}>
          <span style={{ padding: '2px 7px', borderRadius: '4px', background: sm.bg, color: sm.text, fontWeight: 600, fontSize: '11px' }}>
            {sm.label}
          </span>
          {r.state === 'running' && (
            <button onClick={handleAbort} style={{
              marginLeft: '6px', padding: '1px 6px', fontSize: '10px', cursor: 'pointer',
              border: '1px solid #fca5a5', borderRadius: '3px', background: '#fff1f2',
              color: '#991b1b', fontWeight: 600,
            }}>強制中断</button>
          )}
        </td>
        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{formatDuration(r.duration_sec)}</td>
        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{r.total_count ?? '—'}</td>
        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{r.done_count}</td>
        <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', color: r.error_count > 0 ? '#991b1b' : '#94a3b8' }}>{r.error_count}</td>
        <td style={{ ...td, color: '#64748b' }}>{r.triggered_by || '—'}</td>
        <td style={{ ...td, fontSize: '11px' }}>
          {hasMessage ? (
            <button onClick={() => setExpanded(v => !v)} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              color: hasError ? '#991b1b' : '#1d4ed8', fontSize: '11px', textDecoration: 'underline dotted',
            }}>
              {expanded ? '▲ 閉じる' : '▼ 詳細を見る'}
            </button>
          ) : (
            <span style={{ color: '#94a3b8' }}>—</span>
          )}
        </td>
      </tr>
      {hasMessage && expanded && (
        <tr style={{ background: i % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
          <td colSpan={9} style={{ padding: '0 12px 10px 12px' }}>
            <pre style={{
              margin: 0, padding: '8px 10px',
              background: hasError ? '#fff5f5' : '#f0fdf4',
              border: `1px solid ${hasError ? '#fecaca' : '#bbf7d0'}`,
              borderRadius: '4px', fontSize: '11px',
              color: hasError ? '#7f1d1d' : '#14532d',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6,
              maxHeight: '400px', overflowY: 'auto',
            }}>
              {r.error_message}
            </pre>
          </td>
        </tr>
      )}
    </>
  )
}

export default function JobRunsIndex({ runs }) {
  return (
    <AppLayout title="ジョブ実行履歴">
      {runs.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '10px', padding: '32px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
          実行履歴はありません
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <th style={th}>開始日時</th>
                <th style={th}>ジョブ</th>
                <th style={th}>状態</th>
                <th style={{ ...th, textAlign: 'right' }}>所要時間</th>
                <th style={{ ...th, textAlign: 'right' }}>件数</th>
                <th style={{ ...th, textAlign: 'right' }}>完了</th>
                <th style={{ ...th, textAlign: 'right' }}>エラー</th>
                <th style={th}>実行者</th>
                <th style={th}>エラー詳細</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r, i) => <RunRow key={r.id} r={r} i={i} />)}
            </tbody>
          </table>
        </div>
      )}
    </AppLayout>
  )
}

const th = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }
const td = { padding: '9px 12px', verticalAlign: 'middle' }
